/**
 * RW-2440 — one move rule for every surface that moves a deal between stages:
 * the board (drag, and [ / ] on a selected card) and the deal record's picker.
 *
 * Moves go through dealsService.changeStatus with STRUCTURAL ladder keys; the
 * server maps them to the stored vocabulary, gates them against the side's
 * ladder (RW-1585) and wakes the checklist on entering Under Contract.
 */
import { daLadder, daStageKeyForDeal, DA_STAGES } from './dealLadder';

/** The changeStatus body for a move from one ladder key to another. */
export function stageMovePayload(fromKey, toKey) {
  if (toKey === 'closed') return { stage: 'closed', outcome: 'closed' };
  // Moving out of Closed re-opens the deal (the RW-1283 re-offer motion).
  if (fromKey === 'closed') return { stage: toKey, outcome: null };
  return { stage: toKey };
}

/** The deal's own ladder — side- and lease-aware keys and labels. */
export function stagesFor(deal) {
  return daLadder(deal && deal.side, deal && deal.deal_type);
}

/** What this deal's ladder calls a key: "Searching", "On Market", "Clear to Close"… */
export function stageLabelFor(deal, key) {
  const own = stagesFor(deal).find((s) => s.key === key);
  return (own && own.label) || (DA_STAGES[key] && DA_STAGES[key].label) || key;
}

/** The stage one step back (-1) or on (+1) along the deal's ladder; null past either end. */
export function adjacentStage(deal, fromKey, step) {
  const keys = stagesFor(deal).map((s) => s.key);
  const i = keys.indexOf(fromKey);
  if (i < 0) return null;
  return keys[i + step] || null;
}

/** Lost deals (cancelled / fell through / expired) are off the ladder; Closed is its last rung. */
export function isOnLadder(deal) {
  return !!deal && (!deal.outcome || deal.outcome === 'closed');
}

// ── RW-4028 (Clint 2026-10-27): the deal's own agent moves it up to Under Contract ──
// Twin of the server's rule (functions/src/domain/deals.js AGENT_STAGE_KEYS /
// agentMayMoveStage — the server is the authority; this only mirrors it so
// the picker offers what will go through). Clear to Close and Closed stay
// staff's; a closed or lost deal isn't the agent's to reopen.
export const AGENT_STAGE_KEYS = Object.freeze(['lead', 'prep', 'active', 'offer', 'contract']);

/**
 * The rungs this viewer may pick for this deal (the current one included, shown
 * as "here"); [] when they can't move it. Staff: the whole ladder of a deal on
 * it. The deal's own agent: the rungs up to Under Contract, from a stage inside
 * that band. Anyone else (a co-agent, a read-only viewer): none.
 */
export function stageTargetsFor(deal, { canManage = false, viewerUid = null } = {}) {
  if (!isOnLadder(deal)) return [];
  const ladder = stagesFor(deal);
  if (canManage) return ladder;
  if (!viewerUid || deal.primary_agent_uid !== viewerUid) return [];
  if (!AGENT_STAGE_KEYS.includes(daStageKeyForDeal(deal))) return [];
  return ladder.filter((s) => AGENT_STAGE_KEYS.includes(s.key));
}
